import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Phone, User, MapPin, Sparkles, CheckCircle2, Loader2, ShieldCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

/**
 * First-time onboarding modal. Triggered when a Google-signed-in user has
 * `profile_complete === false`. Collects Name, Phone (Mock OTP), Address and
 * consents, then auto-applies the admin-configured welcome coupon.
 */
export default function OnboardingModal() {
  const { user, authClient, refreshUser, isPhoneMock } = useAuth();
  const shouldShow = !!user && user.profile_complete === false;
  const [step, setStep] = useState(1); // 1: form, 2: otp, 3: success
  const [name, setName] = useState(user?.name || '');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [addressLabel, setAddressLabel] = useState('Home');
  const [emailConsent, setEmailConsent] = useState(true);
  const [whatsappConsent, setWhatsappConsent] = useState(true);
  const [otp, setOtp] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [welcomeCoupon, setWelcomeCoupon] = useState(null);

  const validPhone = /^[6-9]\d{9}$/.test(phone);
  const canSendOtp = name.trim() && validPhone && address.trim().length >= 10;

  const sendOtp = async () => {
    if (!canSendOtp) {
      if (!name.trim()) return toast.error('Please enter your name');
      if (!validPhone) return toast.error('Enter a valid 10-digit Indian mobile');
      if (address.trim().length < 10) return toast.error('Please enter a complete delivery address');
    }
    setSending(true);
    try {
      await authClient.post('/otp/send', { phone });
      toast.success(isPhoneMock ? 'OTP sent! Use 123456 for testing' : 'OTP sent successfully!');
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setSending(false);
    }
  };

  const submit = async () => {
    if (otp.length !== 6) return toast.error('Enter the 6-digit OTP');
    setVerifying(true);
    try {
      const { data } = await authClient.post('/profile/complete', {
        name: name.trim(),
        phone,
        otp,
        address_line: address.trim(),
        address_label: addressLabel,
        email_consent: emailConsent,
        whatsapp_consent: whatsappConsent,
      });
      setWelcomeCoupon(data.welcome_coupon);
      if (data.welcome_coupon) {
        // Auto-apply for next order
        sessionStorage.setItem(
          'pushpanjali_coupon',
          JSON.stringify({
            code: data.welcome_coupon.code,
            type: data.welcome_coupon.type,
            value: data.welcome_coupon.value,
            discount: 0, // will be calculated on cart
          })
        );
      }
      setStep(3);
      await refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const close = async () => {
    // Mark profile_complete so the modal doesn't nag on every visit.
    // User can still fill their info later from /profile.
    try {
      await authClient.post('/profile/skip-onboarding');
    } catch {}
    await refreshUser();
    setStep(1);
  };

  if (!shouldShow) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 z-[60] flex items-end md:items-center justify-center p-0 md:p-4"
        data-testid="onboarding-modal"
      >
        <motion.div
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 60, opacity: 0 }}
          transition={{ type: 'spring', damping: 24, stiffness: 260 }}
          className="bg-bgmain rounded-t-3xl md:rounded-3xl w-full md:max-w-md max-h-[92vh] overflow-y-auto shadow-2xl"
        >
          {/* Header */}
          <div className="sticky top-0 bg-gradient-primary text-white px-5 py-4 rounded-t-3xl flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-gradient-primary flex items-center justify-center">
                <img src="/pushpanjaliLogoMain.png" alt="Logo" className="w-full h-full object-contain" />
              </div>
              {/* <Sparkles className="w-5 h-5 text-golden" /> */}
              <div>
                <div className="font-heading font-bold text-lg leading-none">Welcome to Pushpanjali!</div>
                <div className="text-golden text-xs font-body">Step {step === 3 ? 3 : step} of 3</div>
              </div>
            </div>
            {step !== 3 && (
              <button
                onClick={close}
                data-testid="onboarding-close-btn"
                className="text-white/70 hover:text-white p-1"
                aria-label="Skip for now"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          <div className="p-5">
            {step === 1 && (
              <div className="space-y-4">
                <p className="text-textsecondary font-body text-sm">
                  Complete your profile to unlock exclusive offers, faster checkout and a warm welcome gift.
                </p>

                {/* Name */}
                <div>
                  <label className="text-xs text-textprimary font-heading font-semibold flex items-center gap-1 mb-1">
                    <User className="w-3.5 h-3.5" /> Full Name <span className="text-maroon">*</span>
                  </label>
                  <input
                    data-testid="onboarding-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Aarav Sharma"
                    className="w-full px-4 py-2.5 border border-borderbeige rounded-xl font-body text-sm focus:outline-none focus:ring-2 focus:ring-saffron/40"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="text-xs text-textprimary font-heading font-semibold flex items-center gap-1 mb-1">
                    <Phone className="w-3.5 h-3.5" /> Mobile Number <span className="text-maroon">*</span>
                  </label>
                  <div className="flex gap-2">
                    <div className="flex items-center px-3 border border-borderbeige rounded-xl bg-bgsecondary text-textsecondary text-sm font-body">+91</div>
                    <input
                      data-testid="onboarding-phone"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      placeholder="10-digit mobile"
                      inputMode="numeric"
                      className="flex-1 px-4 py-2.5 border border-borderbeige rounded-xl font-body text-sm focus:outline-none focus:ring-2 focus:ring-saffron/40"
                    />
                  </div>
                  {phone && !validPhone && <p className="text-xs text-maroon mt-1 font-body">Enter a valid Indian mobile (starts with 6-9)</p>}
                </div>

                {/* Address */}
                <div>
                  <label className="text-xs text-textprimary font-heading font-semibold flex items-center gap-1 mb-1">
                    <MapPin className="w-3.5 h-3.5" /> Delivery Address <span className="text-maroon">*</span>
                  </label>
                  <textarea
                    data-testid="onboarding-address"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="Flat / House no, Street, Landmark, City, Pincode"
                    rows={3}
                    className="w-full px-4 py-2.5 border border-borderbeige rounded-xl font-body text-sm focus:outline-none focus:ring-2 focus:ring-saffron/40 resize-none"
                  />
                  <div className="flex gap-2 mt-2">
                    {['Home', 'Work', 'Other'].map((l) => (
                      <button
                        key={l}
                        type="button"
                        onClick={() => setAddressLabel(l)}
                        data-testid={`onboarding-label-${l.toLowerCase()}`}
                        className={`text-xs px-3 py-1 rounded-full font-heading font-semibold ${addressLabel === l ? 'bg-maroon text-white' : 'bg-white border border-borderbeige text-textsecondary'}`}
                      >
                        {l}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Consents */}
                <div className="space-y-2 bg-white p-3 rounded-xl border border-borderbeige">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="onboarding-email-consent"
                      checked={emailConsent}
                      onChange={(e) => setEmailConsent(e.target.checked)}
                      className="mt-0.5 accent-saffron"
                    />
                    <span className="text-xs font-body text-textsecondary">Email me order updates &amp; occasional offers</span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="onboarding-whatsapp-consent"
                      checked={whatsappConsent}
                      onChange={(e) => setWhatsappConsent(e.target.checked)}
                      className="mt-0.5 accent-saffron"
                    />
                    <span className="text-xs font-body text-textsecondary">Send updates &amp; delivery tracking on WhatsApp</span>
                  </label>
                </div>

                <button
                  onClick={sendOtp}
                  disabled={sending}
                  data-testid="onboarding-send-otp"
                  className="w-full bg-saffron text-white py-3 rounded-2xl font-heading font-bold shadow-warm hover:bg-saffron-dark disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                  {sending ? 'Sending…' : 'Verify Mobile & Continue'}
                </button>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4 text-center">
                <div className="w-14 h-14 mx-auto rounded-full bg-golden/20 flex items-center justify-center">
                  <Phone className="w-6 h-6 text-maroon" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-textprimary">Verify your mobile</h3>
                  <p className="text-textsecondary font-body text-sm">
                    We sent a 6-digit OTP to <b>+91 {phone}</b>
                  </p>
                  {isPhoneMock && <p className="text-[11px] text-textlight font-body mt-1">Tip: use <span className="font-mono">123456</span> for testing</p>}
                </div>
                <input
                  data-testid="onboarding-otp"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit OTP"
                  inputMode="numeric"
                  maxLength={6}
                  autoFocus
                  className="w-full px-4 py-3 border border-borderbeige rounded-xl text-center font-heading font-bold text-2xl tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-saffron/40"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => setStep(1)}
                    className="flex-1 border border-borderbeige text-textsecondary py-2.5 rounded-2xl font-heading font-semibold text-sm"
                    data-testid="onboarding-back"
                  >
                    Back
                  </button>
                  <button
                    onClick={submit}
                    disabled={verifying || otp.length !== 6}
                    data-testid="onboarding-verify-submit"
                    className="flex-[2] bg-saffron text-white py-2.5 rounded-2xl font-heading font-bold shadow-warm disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    {verifying ? 'Verifying…' : 'Verify & Finish'}
                  </button>
                </div>
                <button
                  onClick={sendOtp}
                  disabled={sending}
                  className="text-xs text-maroon font-body underline"
                  data-testid="onboarding-resend-otp"
                >
                  Resend OTP
                </button>
              </div>
            )}

            {step === 3 && (
              <div className="text-center space-y-4 py-2">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', damping: 12 }}
                  className="w-16 h-16 mx-auto rounded-full bg-fresh/20 flex items-center justify-center"
                >
                  <CheckCircle2 className="w-8 h-8 text-fresh" />
                </motion.div>
                <div>
                  <h3 className="font-heading font-extrabold text-xl text-textprimary">You're all set, {name.split(' ')[0]}!</h3>
                  <p className="text-textsecondary font-body text-sm mt-1">Your profile is ready. Now enjoy authentic biryani &amp; curries at your doorstep.</p>
                </div>

                {welcomeCoupon && (
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    data-testid="onboarding-welcome-coupon"
                    className="bg-gradient-offer p-4 rounded-2xl border-2 border-dashed border-maroon/40"
                  >
                    <div className="text-xs text-maroon font-heading font-semibold uppercase tracking-wider">Your welcome gift</div>
                    <div className="font-heading font-extrabold text-2xl text-maroon mt-1">{welcomeCoupon.code}</div>
                    <div className="text-xs text-textsecondary font-body mt-1">{welcomeCoupon.description || (welcomeCoupon.type === 'percent' ? `${welcomeCoupon.value}% off` : `₹${welcomeCoupon.value} off`)}</div>
                    <div className="text-[11px] text-textlight font-body mt-2">Auto-applied on your first order!</div>
                  </motion.div>
                )}

                <button
                  onClick={close}
                  data-testid="onboarding-start-ordering"
                  className="w-full bg-saffron text-white py-3 rounded-2xl font-heading font-bold shadow-warm"
                >
                  Start Ordering
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
